/**
 * The help screen.
 *
 * Static prose, so nothing here reads simulation state and every `update`
 * ignores the snapshot it is handed. Written from the code that implements the
 * controls — src/ui/interaction.ts for the maps, src/ui/console.ts for the rail
 * — rather than from memory, because a help screen that is subtly wrong is
 * worse than none.
 *
 * The introduction used to live here too. It is a title screen now: see
 * src/ui/titleScreen.ts.
 */

import { el, section, type Panel } from './panel';

interface HelpSection {
  title: string;
  lines: readonly (readonly [string, string])[];
}

/**
 * Every control the game actually has, taken from the code that implements
 * them rather than from memory — src/ui/interaction.ts for the maps,
 * src/ui/console.ts for the rail.
 */
const HELP: readonly HelpSection[] = [
  {
    title: 'Getting around',
    lines: [
      ['Rail keys', 'The column down the left. One screen is lit at a time.'],
      ['Arrow keys', 'Left and right — or up and down — rotate through the screens.'],
      ['Theme', 'The button at the top right cycles five colour schemes.'],
    ],
  },
  {
    title: 'World — mining',
    lines: [
      ['Click a hex', 'Selects it. Its colour is its three largest minerals blended, and it fades as the hex is stripped.'],
      ['Send', 'Sends that drone to the selected hex. It strip-mines: you choose the hex, not the mineral.'],
      ['A', 'Auto-route. The drone picks its own hex, now and whenever one runs dry.'],
      ['Auto-route all', 'The same for the whole fleet, which is usually what you want.'],
    ],
  },
  {
    title: 'City — building',
    lines: [
      ['Pick a building', 'From the Build list, then drag across the grid to queue a run of plots.'],
      ['Adjacency', 'Mechs can only build next to something that already stands. The first plots must touch the factory.'],
      ['Right-click', 'Switches a finished building on or off. They finish switched OFF.'],
      ['Shift + right-click', 'Demolishes. The full cost comes back.'],
      ['The queue', 'Works head-first and stops when it cannot afford the next plot, rather than skipping ahead.'],
    ],
  },
  {
    title: 'Factory — refining',
    lines: [
      ['Power', 'Everything draws on it. Short of power, the whole factory throttles together.'],
      ['Recipe buttons', 'Switch a component on or off. All seven start on.'],
      ['keep', 'Stop making one at that many. Blank means no limit.'],
      ['Priority', 'Which recipes get energy first when there is not enough.'],
      ['Assembly bay', 'Builds drones and mechs over real time, one at a time.'],
    ],
  },
  {
    title: 'Colony and stores',
    lines: [
      ['Request colonists', 'Only once housing, food, water and amenity look good — the panel shows what they would think before they arrive.'],
      ['Buildings', 'Switch a whole class on or off at once. Nothing produces while it is off.'],
      ['sell / buy', 'Cycles a standing order. Sell keeps that many in reserve; buy tops up to that many.'],
      ['Minerals', 'Raw ore is not traded — only refined elements and finished components.'],
    ],
  },
  {
    title: 'Sessions',
    lines: [
      ['Title screen', 'On load: New begins a fresh colony, Continue resumes the save.'],
      ['Save and Load', 'Kept in this browser. Clearing site data loses it.'],
      ['Copy report', 'A diagnostic for a bug report: build, seed, and where the colony had got to.'],
      ['?seed=1234', 'Add it to the address to play a different world.'],
    ],
  },
];

export function createHelpPanel(sectionIndex: number): Panel {
  const group = HELP[sectionIndex];
  if (group === undefined) throw new Error(`no help section ${sectionIndex}`);

  const { root, body } = section(group.title);
  for (const [term, description] of group.lines) {
    const row = el('div', 'help-row');
    row.append(el('span', 'help-term', term), el('span', 'help-text', description));
    body.append(row);
  }

  return {
    root,
    update(): void {
      // Static text.
    },
  };
}

/** How many help panels there are, so the console can build them all. */
export const HELP_SECTION_COUNT = HELP.length;

/** The goal, stated once, above the rest of the help. */
export function createObjectivePanel(): Panel {
  const { root, body } = section('The objective');
  body.append(
    el(
      'div',
      'help-text',
      'Fill all 616 buildable plots of the city grid. Everything else — the drones, the refinery, the power budget, the colonists — exists to make that possible or to slow it down. There is no score and no clock: credits are a way to turn surplus into something you need, not a thing to accumulate.',
    ),
  );
  return {
    root,
    update(): void {
      // Static text.
    },
  };
}
